import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { fetchWithBackendFallback } from '../utils/backendApi';

export default function Settings({ user }) {
  const [settings, setSettings] = useState({
    pushNotifications: false,
    telegramId: '',
    monitoringTimes: [],
    analysisTimeframes: []
  });
  const [newTime, setNewTime] = useState({ hour: '12', minute: '00', period: 'AM' });
  const [selectedTimeframe, setSelectedTimeframe] = useState('4h');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const TIMEFRAME_OPTIONS = ['1d', '4h', '1h', '15m', '5m'];

  useEffect(() => {
    fetchSettings();
  }, [user]);

  const convertTo12Hour = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${period}`;
  };

  const convertTo24Hour = (hour, minute, period) => {
    let hour24 = parseInt(hour, 10);
    if (period === 'AM' && hour24 === 12) hour24 = 0;
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  };

  const addTimeframe = () => {
    if (!selectedTimeframe || settings.analysisTimeframes.includes(selectedTimeframe)) return;
    setSettings((prev) => ({
      ...prev,
      analysisTimeframes: [...prev.analysisTimeframes, selectedTimeframe]
    }));
  };

  const removeTimeframe = (timeframeToRemove) => {
    setSettings((prev) => ({
      ...prev,
      analysisTimeframes: prev.analysisTimeframes.filter((tf) => tf !== timeframeToRemove)
    }));
  };

  const addMonitoringTime = () => {
    const time24 = convertTo24Hour(newTime.hour, newTime.minute, newTime.period);
    if (settings.monitoringTimes.includes(time24)) return;
    setSettings((prev) => ({
      ...prev,
      monitoringTimes: [...prev.monitoringTimes, time24].sort()
    }));
  };

  const removeMonitoringTime = (timeToRemove) => {
    setSettings((prev) => ({
      ...prev,
      monitoringTimes: prev.monitoringTimes.filter((time) => time !== timeToRemove)
    }));
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettings({
          pushNotifications: data.push_notifications || false,
          telegramId: data.telegram_id || '',
          monitoringTimes:
            data.monitoring_times || ['00:16', '02:16', '08:16', '12:16', '15:45', '18:45', '21:00', '22:16'],
          analysisTimeframes: data.analysis_timeframes || ['4h', '1h', '15m', '5m']
        });
      } else {
        setSettings((prev) => ({
          ...prev,
          monitoringTimes: ['00:16', '02:16', '08:16', '12:16', '15:45', '18:45', '21:00', '22:16'],
          analysisTimeframes: ['4h', '1h', '15m', '5m']
        }));
      }
    } catch (settingsError) {
      console.error('Error fetching settings:', settingsError);
      setMessage('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage('');

    try {
      const { response } = await fetchWithBackendFallback('/settings/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          push_notifications: settings.pushNotifications,
          telegram_id: settings.telegramId,
          monitoring_times: settings.monitoringTimes,
          analysis_timeframes: settings.analysisTimeframes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save settings');
      }

      setMessage('Settings saved successfully.');
      setTimeout(() => setMessage(''), 4000);
    } catch (saveError) {
      console.error('Error saving settings:', saveError);
      setMessage(`Error saving settings: ${saveError.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="app-page">
        <section className="card">
          <div className="status-card">
            <div className="spinner" />
            <div>
              <h3>Loading settings</h3>
              <p>Fetching your monitoring and notification preferences...</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="app-page space-y-6">
      <section className="card">
        <div className="page-head">
          <div>
            <p className="eyebrow">Account</p>
            <h1 className="page-title">Settings</h1>
            <p className="page-subtitle">Configure notifications, watchlist monitoring schedule, and analysis timeframes.</p>
          </div>
        </div>

        {message && (
          <div className={message.includes('Error') ? 'error-banner' : 'success-box'}>
            <p>{message}</p>
          </div>
        )}

        <div className="settings-grid mt-4">
          <article className="insight-panel">
            <h4>Notifications</h4>
            <label className="switch-row">
              <input
                type="checkbox"
                checked={settings.pushNotifications}
                onChange={(event) =>
                  setSettings((prev) => ({ ...prev, pushNotifications: event.target.checked }))
                }
              />
              <span>Enable push notifications</span>
            </label>
            <label className="control-label mt-3">Telegram User ID</label>
            <input
              type="text"
              value={settings.telegramId}
              onChange={(event) =>
                setSettings((prev) => ({ ...prev, telegramId: event.target.value }))
              }
              placeholder="Enter your Telegram user ID"
              className="input-field"
            />
            <p className="text-xs text-text-muted mt-2">
              Use <a className="link-btn" href="https://t.me/crypnai_bot" target="_blank" rel="noopener noreferrer">@crypnai_bot</a> and send <code>/start</code> to get your ID.
            </p>
          </article>

          <article className="insight-panel">
            <h4>Monitoring Times (ET)</h4>
            <div className="chip-wrap">
              {settings.monitoringTimes.map((time) => (
                <span key={time} className="soft-pill">
                  {convertTo12Hour(time)}
                  <button type="button" onClick={() => removeMonitoringTime(time)} className="pill-close">×</button>
                </span>
              ))}
            </div>

            <div className="time-input-grid mt-3">
              <select
                value={newTime.hour}
                onChange={(event) => setNewTime((prev) => ({ ...prev, hour: event.target.value }))}
                className="input-field"
              >
                {Array.from({ length: 12 }, (_, idx) => idx + 1).map((hour) => (
                  <option key={hour} value={hour.toString().padStart(2, '0')}>
                    {hour}
                  </option>
                ))}
              </select>
              <select
                value={newTime.minute}
                onChange={(event) => setNewTime((prev) => ({ ...prev, minute: event.target.value }))}
                className="input-field"
              >
                {Array.from({ length: 60 }, (_, idx) => idx).map((minute) => (
                  <option key={minute} value={minute.toString().padStart(2, '0')}>
                    {minute.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
              <select
                value={newTime.period}
                onChange={(event) => setNewTime((prev) => ({ ...prev, period: event.target.value }))}
                className="input-field"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
              <button type="button" className="btn-secondary" onClick={addMonitoringTime}>
                Add Time
              </button>
            </div>
          </article>

          <article className="insight-panel">
            <h4>Analysis Timeframes</h4>
            <div className="chip-wrap">
              {settings.analysisTimeframes.map((timeframe) => (
                <span key={timeframe} className="soft-pill">
                  {timeframe}
                  <button type="button" onClick={() => removeTimeframe(timeframe)} className="pill-close">×</button>
                </span>
              ))}
            </div>

            <div className="time-input-grid mt-3">
              <select
                value={selectedTimeframe}
                onChange={(event) => setSelectedTimeframe(event.target.value)}
                className="input-field"
              >
                {TIMEFRAME_OPTIONS.map((timeframe) => (
                  <option key={timeframe} value={timeframe}>
                    {timeframe}
                  </option>
                ))}
              </select>
              <button type="button" className="btn-secondary" onClick={addTimeframe}>
                Add Timeframe
              </button>
            </div>
          </article>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={saveSettings} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </section>
    </main>
  );
}
